// ./src/QrScanner/scanGeometry.ts
//
// Where the scanner looks — ONE copy of the geometry, shared by the on-screen framing guide and
// the region that is actually decoded (ROADMAP #65 d, owner 2026-09-28).
//
// ⚠⚠ WHY IT EXISTS. The scanner used to decode the WHOLE camera frame while the preview showed a
// `object-fit: cover` crop of it with a CSS-only guide on top. So a barcode above or below the one
// in the guide — even one cropped OFF the preview entirely — could win, and the operator received
// an item they could not see. What is decoded is now computed from the same numbers that draw the
// guide, so the two cannot disagree.
//
// ⚠ Pure and import-free: `scripts/checkInventoryScan.ts` (apps/app) RUNS it.

export type ScanGuide = "square" | "wide";

/** Which part of the picture may produce a code. */
export type ScanDecodeArea =
  /** Only the framing guide (plus a small margin) — for dense shelves of barcodes. */
  | "guide"
  /** Everything the preview SHOWS, and nothing the `cover` crop hides — the default. */
  | "visible";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The guide's shape, as it is DRAWN. */
export const GUIDE_SPEC: Record<ScanGuide, { widthFrac: number; aspect: number; maxWidth: number }> = {
  // ⓘ Generous: the door reads only inside this frame, and a code a little too close must still fit (Scandit: "do not
  //   over-restrict the area, or the user feels they are scanning through a keyhole"). A neighbour in it loses on distance.
  square: { widthFrac: 0.74, aspect: 1, maxWidth: 300 },
  wide: { widthFrac: 0.84, aspect: 2.6, maxWidth: 340 },
};

/** The guide never takes more than this share of the preview's height (a short preview on a phone). */
export const GUIDE_MAX_HEIGHT_FRAC = 0.86;

/**
 * The margin decoded around the guide, as a share of its size. A 1D barcode needs a blank "quiet
 * zone" beside its bars, so a code that fills the frame edge to edge would fail on an exact crop.
 * Wider across than down: the neighbours that caused wrong reads sit ABOVE and BELOW.
 */
export const GUIDE_MARGIN = { x: 0.12, y: 0.08 };

/** The guide rectangle, in preview (CSS) pixels, centred. */
export const guideRect = (containerW: number, containerH: number, guide: ScanGuide): Rect => {
  const spec = GUIDE_SPEC[guide];
  let w = Math.min(containerW * spec.widthFrac, spec.maxWidth);
  let h = w / spec.aspect;
  const maxH = containerH * GUIDE_MAX_HEIGHT_FRAC;

  if (h > maxH) {
    h = maxH;
    w = h * spec.aspect;
  }

  return { x: (containerW - w) / 2, y: (containerH - h) / 2, w, h };
};

/** The region to decode, in preview (CSS) pixels — never outside the preview itself. */
export const decodeRegion = (containerW: number, containerH: number, guide: ScanGuide, area: ScanDecodeArea): Rect => {
  if (area === "visible") {
    return { x: 0, y: 0, w: containerW, h: containerH };
  }

  const g = guideRect(containerW, containerH, guide);
  const mx = g.w * GUIDE_MARGIN.x;
  const my = g.h * GUIDE_MARGIN.y;
  const x0 = Math.max(0, g.x - mx);
  const y0 = Math.max(0, g.y - my);
  const x1 = Math.min(containerW, g.x + g.w + mx);
  const y1 = Math.min(containerH, g.y + g.h + my);

  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
};

/**
 * Map a rectangle on the preview to the camera frame's own pixels, through `object-fit: cover`
 * (the video is scaled to FILL the preview and centred, so its overflow is cut off on two sides).
 * Clamped to the frame; null when nothing is left or a size is not known yet.
 */
export const previewRectToVideo = (rect: Rect, containerW: number, containerH: number, videoW: number, videoH: number): Rect | null => {
  if (!(containerW > 0 && containerH > 0 && videoW > 0 && videoH > 0)) {
    return null;
  }

  const scale = Math.max(containerW / videoW, containerH / videoH);
  const offsetX = (containerW - videoW * scale) / 2;
  const offsetY = (containerH - videoH * scale) / 2;

  const x0 = Math.max(0, (rect.x - offsetX) / scale);
  const y0 = Math.max(0, (rect.y - offsetY) / scale);
  const x1 = Math.min(videoW, (rect.x + rect.w - offsetX) / scale);
  const y1 = Math.min(videoH, (rect.y + rect.h - offsetY) / scale);

  if (x1 <= x0 || y1 <= y0) {
    return null;
  }

  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
};

/** The size to decode a crop at — full resolution, but never wider than `maxWidth` (decode cost). */
export const decodeSize = (crop: Rect, maxWidth: number): { w: number; h: number } => {
  const scale = Math.min(1, maxWidth / crop.w);

  return { w: Math.max(1, Math.round(crop.w * scale)), h: Math.max(1, Math.round(crop.h * scale)) };
};

